/* global Modal, theme */

if (!customElements.get('paired-products-popup')) {
  customElements.whenDefined('modal-dialog').then(() => {
    const STORAGE_KEY = 'paired-products-popup:shown';
    const CLOSE_AFTER_ADD_DELAY = 600;

    class PairedProductsPopup extends Modal {
      constructor() {
        super();
        this.contentEl = this.querySelector('[data-paired-popup-content]');
        this.cartAddHandler = this.handleCartAdd.bind(this);
      }

      connectedCallback() {
        document.addEventListener('on:cart:add', this.cartAddHandler);
        this.addEventListener('submit', this.handleSubmit.bind(this));
      }

      disconnectedCallback() {
        document.removeEventListener('on:cart:add', this.cartAddHandler);
        clearTimeout(this.closeTimeout);
      }

      close() {
        clearTimeout(this.closeTimeout);
        super.close();
      }

      /**
       * Handles an add to cart from anywhere else on the page.
       * @param {object} evt - Event object, with a variantId in its detail.
       */
      async handleCartAdd(evt) {
        // Never react to an add made from inside the popup itself.
        if (this.hasAttribute('open') || this.contains(evt.target)) return;

        const variantId = evt.detail && evt.detail.variantId;
        if (!variantId) return;

        try {
          const item = await this.getCartItem(variantId);
          if (!item || this.alreadyShownFor(item.handle)) return;

          const cards = await this.getPairedProducts(item);
          if (!cards) return;

          this.contentEl.innerHTML = '';
          this.contentEl.appendChild(cards);
          this.markShownFor(item.handle);
          this.dismissAddedNotification();
          this.open();
        } catch (error) {
          console.log(error); // eslint-disable-line no-console
        }
      }

      /**
       * Finds the cart line item for a variant, for its product handle and id.
       * @param {number} variantId - Variant that was just added.
       * @returns {?object} Cart line item.
       */
      async getCartItem(variantId) {
        const response = await fetch(`${theme.routes.cart}.js`, {
          headers: { Accept: 'application/json' }
        });
        if (!response.ok) return null;

        const cart = await response.json();
        return cart.items.find((cartItem) => cartItem.variant_id === Number(variantId)) || null;
      }

      /**
       * Builds the URL the paired product cards are fetched from.
       * @param {object} item - Cart line item for the added product.
       * @returns {URL} Section rendering URL.
       */
      getContentUrl(item) {
        if (this.dataset.source === 'metafield') {
          const url = new URL(`${window.Shopify.routes.root}products/${item.handle}`, window.location.origin);
          url.searchParams.set('section_id', this.dataset.sectionId);
          return url;
        }

        // Complementary products, as set with the Search & Discovery app.
        const url = new URL(this.dataset.recommendationsUrl, window.location.origin);
        url.searchParams.set('section_id', this.dataset.sectionId);
        url.searchParams.set('product_id', item.product_id);
        url.searchParams.set('intent', 'complementary');
        url.searchParams.set('limit', this.dataset.limit || 3);
        return url;
      }

      /**
       * Fetches the paired product cards for a product.
       * @param {object} item - Cart line item for the added product.
       * @returns {?DocumentFragment} Cards, or null if the product has no pairings.
       */
      async getPairedProducts(item) {
        const url = this.getContentUrl(item);

        const response = await fetch(url.toString());
        if (!response.ok) return null;

        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const grid = doc.querySelector('[data-paired-products-grid]');
        if (!grid || grid.children.length === 0) return null;

        const columns = Math.min(Number(this.dataset.columns) || 3, grid.children.length);
        grid.classList.add(`paired-popup__grid--${columns}`);

        const fragment = document.createDocumentFragment();
        fragment.appendChild(grid);
        return fragment;
      }

      /**
       * Dismisses the "added to cart" toast, which would otherwise sit on top of
       * the popup saying the same thing. The cart drawer is left alone - the
       * popup stacks above it.
       */
      dismissAddedNotification() {
        document.querySelectorAll('.added-notification').forEach((el) => el.dismiss());
      }

      /**
       * @param {string} handle - Product handle.
       * @returns {boolean} Whether the popup has already been shown for this product.
       */
      alreadyShownFor(handle) {
        if (this.dataset.oncePerProduct !== 'true') return false;
        return this.getShownHandles().includes(handle);
      }

      /**
       * Records that the popup has been shown for a product.
       * @param {string} handle - Product handle.
       */
      markShownFor(handle) {
        if (this.dataset.oncePerProduct !== 'true') return;

        const handles = this.getShownHandles();
        handles.push(handle);
        try {
          sessionStorage.setItem(STORAGE_KEY, JSON.stringify(handles));
        } catch (error) {
          this.shownHandles = handles;
        }
      }

      /**
       * @returns {Array} Handles the popup has been shown for this session.
       */
      getShownHandles() {
        try {
          return JSON.parse(sessionStorage.getItem(STORAGE_KEY)) || [];
        } catch (error) {
          return this.shownHandles || [];
        }
      }

      /**
       * Handles submission of an "add to cart" form inside the popup.
       * @param {object} evt - Event object.
       */
      async handleSubmit(evt) {
        const form = evt.target.closest('.js-paired-add-form');
        if (!form) return;

        evt.preventDefault();
        const btn = form.querySelector('[name="add"]');
        if (btn.getAttribute('aria-disabled') === 'true') return;

        btn.setAttribute('aria-disabled', 'true');
        btn.classList.add('is-loading');

        const formData = new FormData(form);
        const sectionsToUpdate = ['page-header', 'cart-drawer']
          .map((sel) => document.querySelector(sel))
          .filter((el) => el);

        formData.append('sections_url', window.location.pathname);
        formData.append('sections', sectionsToUpdate.map((el) => el.dataset.sectionId).join(','));

        try {
          const response = await fetch(theme.routes.cartAdd, {
            method: 'POST',
            headers: {
              Accept: 'application/javascript',
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: formData
          });
          const data = await response.json();
          if (!response.ok) throw new Error(response.status);

          if (data.sections && sectionsToUpdate.length === Object.keys(data.sections).length) {
            sectionsToUpdate.forEach((el) => el.updateFromCartChange(data.sections[el.dataset.sectionId]));
          } else {
            document.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true }));
          }

          btn.classList.remove('is-loading');
          btn.classList.add('is-success');
          const btnText = btn.querySelector('.paired-popup__card-btn-text');
          if (btnText && btn.dataset.addedText) btnText.textContent = btn.dataset.addedText;

          this.closeTimeout = setTimeout(() => this.close(), CLOSE_AFTER_ADD_DELAY);
        } catch (error) {
          console.log(error); // eslint-disable-line no-console
          btn.classList.remove('is-loading');
          btn.removeAttribute('aria-disabled');
        }
      }
    }

    customElements.define('paired-products-popup', PairedProductsPopup);
  });
}
